import {
  api,
  faceSwapApi,
  videoFaceSwapApi,
  backgroundChangeUrl,
  colorizeUrl,
  ErosAiUrl,
  voiceCloneUrl,
  SpiritualAssesmentUrl,
  actionGenerationUrl,
  imageToVideoUrl,
  textToImageApi,
  textToVideoApi,
  voiceRemixApi,
  talkingAvatarApi,
  toolCreditApi,
  userAppApi,
  socialApi,
  socialDashboardApi,
  assetApi
} from '../utils/axiosInstance'
import React, { useEffect } from 'react'
import { useResponsive } from '../hooks'
import { media } from '../utils/media'
import { v4 as uuidv4 } from 'uuid'
import { ERROR_CODES } from 'shared-ui/utils/constants'
import { getUserLanguage } from '../utils/localStorage'

// Simple in-memory cache and deduplication for GET requests
const apiGetCache = new Map()
const apiGetInFlight = new Map()
const API_CACHE_TTL = 60 * 1000 // 1 minute

function getCacheKey(url, params) {
  const lang = getUserLanguage()
  return url + ':' + JSON.stringify(params || {}) + ':lang=' + (lang || '')
}

function setApiCache(key, value) {
  apiGetCache.set(key, { value, ts: Date.now() })
}

function getApiCache(key) {
  const entry = apiGetCache.get(key)
  if (!entry) return undefined
  if (Date.now() - entry.ts > API_CACHE_TTL) {
    apiGetCache.delete(key)
    return undefined
  }
  return entry.value
}

function getInFlightPromise(key) {
  return apiGetInFlight.get(key)
}

function setInFlightPromise(key, promise) {
  apiGetInFlight.set(key, promise)
}

function clearInFlightPromise(key) {
  apiGetInFlight.delete(key)
}

/**
 * Clears the in-memory GET cache and any in-flight promises.
 * Call this when global state affecting API responses changes (e.g., language).
 */
export function clearApiGetCache() {
  try {
    apiGetCache.clear()
    apiGetInFlight.clear()
  } catch (e) {
    console.warn('Failed to clear API GET cache', e)
  }
}
import axios from 'axios'

let isMobileInternal = false

export const ApiConfigProvider = ({ children }) => {
  const isMobile = useResponsive(media.mobile)

  useEffect(() => {
    isMobileInternal = isMobile
  }, [isMobile])

  return <>{children}</>
}

export const getCommonHeaders = (custom = {}) => {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('access') : ''
  const lang = getUserLanguage()
  const userId = JSON.parse(localStorage.getItem('user'))?.user_id
  return {
    'x-platform': 'web',
    'x-version': '1.0.0',
    'x-appname': 'eros-create',
    'x-request-id': uuidv4(),
    'x-api-token': token,
    'x-language': lang,
    'x-user-id': userId,
    ...custom
  }
}

export function parseApiError(errorData) {
  if (!errorData) {
    return { message: 'An unexpected error occurred' }
  }

  if (typeof errorData === 'string') {
    return { message: errorData }
  }

  if (typeof errorData === 'object') {
    if (errorData.error) {
      return {
        message: errorData.error.message || 'Something went wrong',
        code: errorData.error.error_code,
        status: errorData.error.code,
        type: errorData.error.type
      }
    }

    if (Array.isArray(errorData.detail)) {
      return {
        message: errorData.detail.map((err) => {
          if (typeof err === 'string') return err
          if (err.msg) {
            const field =
              err.loc && err.loc.length > 1
                ? err.loc[err.loc.length - 1]
                : 'field'
            return `${field}: ${err.msg}`
          }
          return JSON.stringify(err)
        }).join('; ')
      }
    }

    if (errorData.detail) return { message: errorData.detail }
    if (errorData.message) return { message: errorData.message }

    if (errorData.data) {
      return parseApiError(errorData.data)
    }

    if (typeof errorData.error === 'string') {
      return { message: errorData.error }
    }

    return { message: JSON.stringify(errorData) }
  }

  return { message: 'An unexpected error occurred' }
}

/**
 * @typedef {Object} ApiRequestConfig
 * @property {'get' | 'post' | 'put' | 'patch' | 'delete'} [method] - HTTP method.
 * @property {string} url - API endpoint.
 * @property {Object} [data] - Request body (for POST/PUT).
 * @property {Object} [params] - URL query parameters.
 * @property {Object} [headers] - Custom headers.
 */

/**
 * Makes an API request using the main axios instance.
 * @param {ApiRequestConfig} config - The request configuration.
 * @returns {Promise<any>} - API response data.
 */
export const apiRequest = async ({ method, url, data, params, headers, skipCache }) => {
  const isGet = (method || 'get').toLowerCase() === 'get'
  const cacheKey = isGet ? getCacheKey(url, params) : null
  if (isGet && !skipCache) {
    const cached = getApiCache(cacheKey)
    if (cached !== undefined) return cached
    const inFlight = getInFlightPromise(cacheKey)
    if (inFlight) return inFlight
  }
  let requestPromise
  try {
    requestPromise = api.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || { 'Content-Type': 'application/json' }
    }).then((response) => {
      if (isGet) setApiCache(cacheKey, response.data)
      return response.data
    }).finally(() => {
      if (isGet) clearInFlightPromise(cacheKey)
    })
    if (isGet) setInFlightPromise(cacheKey, requestPromise)
    return await requestPromise
  } catch (error) {
    if (isGet) clearInFlightPromise(cacheKey)
    console.error('API Request Error:', error)
    if (error?.response?.data) {
      const parsedError = parseApiError(error.response.data)

      const err = new Error(parsedError.message)
      err.code = parsedError.code
      err.status = parsedError.status
      err.type = parsedError.type

      throw parsedError
    }
    throw new Error(error.message || 'An unexpected error occurred')
  }
}

/**
 * Makes an API request to the Face Swap service.
 * @param {ApiRequestConfig} config - The request configuration.
 * @returns {Promise<any>} - API response data.
 */
export const faceSwapApiRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await faceSwapApi.request({
      method: method || 'post',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })
    return response.data
  } catch (error) {
    console.error('Face Swap API Request Error:', error)

    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }
      throw new Error(JSON.stringify(respData?.error))
    }

    throw new Error(error.message || 'Face swap request failed')
  }
}

/**
 * Makes an API request to the Video Face Swap service.
 * @param {ApiRequestConfig} config - The request configuration.
 * @returns {Promise<any>} - API response data.
 */
export const videoFaceSwapApiRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await videoFaceSwapApi.request({
      method: method || 'post',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })
    return response.data
  } catch (error) {
    console.error('Video Face Swap API Request Error:', error?.detail ?? error)

    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }

      throw new Error(JSON.stringify(respData?.error))
    }

    throw new Error(error.message || 'Video face swap request failed')
  }
}

export const bgchangeRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await backgroundChangeUrl.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })
    return response.data
  } catch (error) {
    console.error('API Request Error:', error)

    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }
      throw new Error(JSON.stringify(respData?.error))
    }

    throw new Error(error.message || 'An unexpected error occurred')
  }
}

export const colorizeRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const isFormData = data instanceof FormData
    const response = await colorizeUrl.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        ...getCommonHeaders(),
        ...(isFormData ? {} : { 'Content-Type': 'application/json' })
      }
    })
    return response.data
  } catch (error) {
    console.error('API Request Error:', error)

    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      // For AI tools, prefer the inner data.error/message over the outer message
      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }
      throw new Error(JSON.stringify(respData?.error))
    }

    throw new Error(error.message || 'An unexpected error occurred')
  }
}
export const voiceCloneRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await voiceCloneUrl.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })
    return response.data
  } catch (error) {
    console.error('API Request Error:', error)

    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }

      throw new Error(JSON.stringify(respData?.error))
    }

    throw new Error(error.message || 'An unexpected error occurred')
  }
}

export const ErosAiRequest = async ({ method, url, data, params, headers }) => {
  try {
    const accessToken = localStorage.getItem('access')
    const authHeaders = accessToken
      ? { Authorization: `Bearer ${accessToken}` }
      : {}
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'x-platform': 'web',
      'x-version': '1.0.0',
      'x-appname': 'create',
      'Content-Type': 'application/json'
    }

    const response = await ErosAiUrl.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: { ...defaultHeaders, ...headers }
    })
    return response.data
  } catch (error) {
    if (error.response) {
      throw {
        status: error.response.status,
        message: error.response.data?.message || 'Server Error',
        data: error.response.data
      }
    }

    if (error.request) {
      throw {
        status: null,
        message:
          'No response from server. Please check your internet connection.'
      }
    }

    throw {
      status: null,
      message: error.message || 'something went wrong'
    }
  }
}

export const SpiritualAssesmentRequest = async ({
  method,
  url,
  data,
  params,
  headers = {},
  onUploadProgress
}) => {
  try {
    // ✅ Detect FormData automatically
    const isFormData = data instanceof FormData

    const response = await SpiritualAssesmentUrl.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: {
        // ✅ Automatically pick correct content type
        'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
        ...headers // allow manual override
      },
      ...(isFormData && onUploadProgress ? { onUploadProgress } : {})
    })

    return response.data
  } catch (error) {
    if (error.response) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (typeof respData === 'object' &&
          (respData.message || respData.detail)) ||
        (nestedData && (nestedData.message || nestedData.detail)) ||
        'Server Error'

      throw {
        status: error.response.status,
        message,
        data: nestedData || respData
      }
    }

    if (error.request) {
      throw {
        status: null,
        message:
          'No response from server. Please check your internet connection.'
      }
    }

    throw {
      status: null,
      message: error.message || 'Something went wrong'
    }
  }
}

export const actionGenerationRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await actionGenerationUrl.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })
    return response.data
  } catch (error) {
    console.error('API Request Error:', error)

    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }

      throw new Error(JSON.stringify(respData?.error))
    }

    throw new Error(error.message || 'An unexpected error occurred')
  }
}

export const imageToVideoRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await imageToVideoUrl.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })

    const respData = response.data

    // Some AI tools return success=false with an error payload in data{}
    if (
      respData &&
      typeof respData === 'object' &&
      respData.success === false
    ) {
      const nestedData =
        typeof respData.data === 'object' && respData.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        respData.message ||
        respData.error ||
        respData.detail ||
        'Image to video request failed'

      throw new Error(message)
    }

    return respData
  } catch (error) {
    console.error('Image to video API error:', error)
    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }

      throw new Error(JSON.stringify(respData?.error))
    }
    throw new Error(error.message || 'An unexpected error occurred')
  }
}

export const textToImageRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await textToImageApi.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })

    const respData = response.data

    if (
      respData &&
      typeof respData === 'object' &&
      respData.success === false
    ) {
      const nestedData =
        typeof respData.data === 'object' && respData.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        respData.message ||
        respData.error ||
        respData.detail ||
        'Text to image request failed'

      throw new Error(message)
    }

    return respData
  } catch (error) {
    console.error('Text to image API error:', error)
    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }

      throw new Error(JSON.stringify(respData?.error))
    }
    throw new Error(error.message || 'An unexpected error occurred')
  }
}

export const textToVideoRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await textToVideoApi.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })

    const respData = response.data

    if (
      respData &&
      typeof respData === 'object' &&
      respData.success === false
    ) {
      const nestedData =
        typeof respData.data === 'object' && respData.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        respData.message ||
        respData.error ||
        respData.detail ||
        'Text to video request failed'

      throw new Error(message)
    }

    return respData
  } catch (error) {
    console.error('Text to video API error:', error)
    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }

      throw new Error(JSON.stringify(respData?.error))
    }
    throw new Error(error.message || 'An unexpected error occurred')
  }
}

export const voiceRemixRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await voiceRemixApi.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })

    const respData = response.data

    if (
      respData &&
      typeof respData === 'object' &&
      respData.success === false
    ) {
      const nestedData =
        typeof respData.data === 'object' && respData.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        respData.message ||
        respData.error ||
        respData.detail ||
        'Voice remix request failed'

      throw new Error(message)
    }

    return respData
  } catch (error) {
    console.error('Voice remix API error:', error)
    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }

      throw new Error(JSON.stringify(respData?.error))
    }
    throw new Error(error.message || 'An unexpected error occurred')
  }
}

export const talkingAvatarRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await talkingAvatarApi.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        ...getCommonHeaders()
      }
    })

    const respData = response.data

    if (
      respData &&
      typeof respData === 'object' &&
      respData.success === false
    ) {
      const nestedData =
        typeof respData.data === 'object' && respData.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        respData.message ||
        respData.error ||
        respData.detail ||
        'Talking avatar request failed'

      throw new Error(message)
    }

    return respData
  } catch (error) {
    console.error('Talking avatar API error:', error)
    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      // if (message) {
      //   throw new Error(message)
      // }

      throw new Error(JSON.stringify(respData?.error))
    }
    throw new Error(error.message || 'An unexpected error occurred')
  }
}

export const toolCreditRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const accessToken = localStorage.getItem('access')
    const response = await toolCreditApi.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json',
        'x-api-token': accessToken,
        Authorization: `Bearer ${accessToken}`,
        'x-api-client': 'CZgbPYnmcj5iyEH9tg0GYvB4lm9gGQ9qs6jQwllV',
        ...getCommonHeaders()
      }
    })
    return response.data?.data
  } catch (error) {
    console.error('Tool Credit API Request Error:', error)

    if (error?.response?.data) {
      const respData = error.response.data
      const nestedData =
        typeof respData === 'object' && respData?.data
          ? respData.data
          : undefined

      const message =
        (nestedData &&
          (nestedData.message || nestedData.error || nestedData.detail)) ||
        (typeof respData === 'object' &&
          (respData.detail || respData.message || respData.error)) ||
        (typeof respData === 'string' ? respData : null)

      if (message) {
        throw new Error(message)
      }
      throw new Error(JSON.stringify(respData))
    }

    throw new Error(error.message || 'Tool credit request failed')
  }
}

export const userAppApiRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  const isFormData =
    data &&
    (data instanceof FormData ||
      typeof data.append === 'function' ||
      Object.prototype.toString.call(data) === '[object FormData]')

  const finalHeaders = headers
    ? { ...headers }
    : isFormData
      ? {}
      : { 'Content-Type': 'application/json' }

  if (isFormData) {
    if (finalHeaders['Content-Type']) delete finalHeaders['Content-Type']
    if (finalHeaders['content-type']) delete finalHeaders['content-type']
  }

  try {
    const response = await userAppApi.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: finalHeaders
    })
    return response.data
  } catch (error) {
    console.error('User App API Request Error:', error)

    if (error?.response?.data) {
      const parsedError = parseApiError(error.response.data)
      const err = new Error(parsedError)
      err.code = parsedError.code
      err.status = parsedError.status
      err.type = parsedError.type

      throw parsedError
    }

    // Handle network errors
    if (error.request && !error.response) {
      throw new Error('Network error. Please check your internet connection.')
    }

    throw new Error(error.message || 'User app request failed')
  }
}

export const socialApiRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await socialApi.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json'
      }
    })
    const respData = response.data
    if (
      respData &&
      typeof respData === 'object' &&
      respData.success === false
    ) {
      const errorMessage = parseApiError(respData)
      throw new Error(errorMessage || 'Social API request failed')
    }
    return respData
  } catch (error) {
    console.error('Social API Request Error:', error)

    if (error?.response?.data) {
      const parsedError = parseApiError(error.response.data)

      const err = new Error(parsedError.message)
      err.code = parsedError.code
      err.status = parsedError.status
      err.type = parsedError.type

      throw parsedError
    }

    if (error.request && !error.response) {
      throw new Error('Network error. Please check your internet connection.')
    }

    throw new Error(error.message || 'Social API request failed')
  }
}

export const socialDashboardApiRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await socialDashboardApi.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json'
      }
    })
    const respData = response.data
    if (
      respData &&
      typeof respData === 'object' &&
      respData.success === false
    ) {
      const errorMessage = parseApiError(respData)
      throw new Error(errorMessage || 'Social API request failed')
    }
    return respData
  } catch (error) {
    console.error('Social API Request Error:', error)

    if (error?.response?.data) {
      const parsedError = parseApiError(error.response.data)

      const err = new Error(parsedError.message)
      err.code = parsedError.code
      err.status = parsedError.status
      err.type = parsedError.type

      throw parsedError
    }

    if (error.request && !error.response) {
      throw new Error('Network error. Please check your internet connection.')
    }

    throw new Error(error.message || 'Social API request failed')
  }
}

export const assetApiRequest = async ({
  method,
  url,
  data,
  params,
  headers
}) => {
  try {
    const response = await assetApi.request({
      method: method || 'get',
      url,
      data,
      params,
      headers: headers || {
        'Content-Type': 'application/json'
      }
    })
    const respData = response.data
    if (
      respData &&
      typeof respData === 'object' &&
      respData.success === false
    ) {
      const errorMessage = parseApiError(respData)
      throw new Error(errorMessage || 'Asset API request failed')
    }
    return respData
  } catch (error) {
    console.error('Asset API Request Error:', error)

    if (error?.response?.data) {
      const parsedError = parseApiError(error.response.data)

      const err = new Error(parsedError.message)
      err.code = parsedError.code
      err.status = parsedError.status
      err.type = parsedError.type

      throw parsedError
    }

    if (error.request && !error.response) {
      throw new Error('Network error. Please check your internet connection.')
    }

    throw new Error(error.message || 'Asset API request failed')
  }
}
